import {
    Alert,
    Box,
    CircularProgress,
    Container,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import WorkspaceLogo from '~/components/Logo/WorkspaceLogo';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import {
    clearLoginRedirectFailure,
    hasLoginRedirectFailed,
    isSigningOut,
} from '~/lib/auth';
import AppConfig from '~/utils/appconfig';

/**
 * The page to return to after sign-in. The path comes from the URL the user
 * opened, and paths like `//host` or `/\host` resolve to another origin, so
 * anything that leaves this origin, or cannot be parsed, falls back to '/'
 * (open redirect guard).
 */
export function loginReturnPath(from?: Partial<Location>): string {
    if (!from?.pathname) {
        return '/';
    }
    const path = `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
    const origin = window.location.origin;
    try {
        return new URL(path, origin).origin === origin ? path : '/';
    } catch {
        // e.g. '//[' parses as an invalid host and throws
        return '/';
    }
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isPending, logIn } = useAuthSession();
    // A sign-in that has just come back incomplete counts as the attempt, so
    // the next one waits for the user instead of starting on sight.
    const [errorMsg, setErrorMsg] = useState<string | undefined>(() =>
        hasLoginRedirectFailed()
            ? 'Sign-in did not complete. Please try again.'
            : undefined
    );
    const [hasTriedLogin, setHasTriedLogin] = useState(hasLoginRedirectFailed);

    useEffect(() => {
        // A sign-out drops the session before the browser leaves for the
        // provider, and the route guard lands here in that gap. Signing in
        // again would be granted silently by the provider session the
        // sign-out has not reached yet, undoing it.
        if (isAuthenticated || isPending || hasTriedLogin || isSigningOut()) {
            return;
        }
        setHasTriedLogin(true);
        // Resolves only as the browser navigates to the provider, so a
        // rejection here means the redirect never started. The attempt stays
        // counted: the next one waits for the user to ask.
        logIn().catch(() => {
            setErrorMsg('Login failed. Please try again.');
        });
    }, [isAuthenticated, isPending, hasTriedLogin, logIn]);

    useEffect(() => {
        if (isAuthenticated) {
            // Redirect to the page user was trying to access, or default to '/'
            navigate(loginReturnPath(location.state?.from), { replace: true });
        }
    }, [isAuthenticated, navigate, location.state]);

    const handleRetry = () => {
        clearLoginRedirectFailure();
        setErrorMsg(undefined);
        setHasTriedLogin(false); // Trigger a new login attempt
    };

    return (
        <Container maxWidth="sm">
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                textAlign="center"
                gap={4}
            >
                <WorkspaceLogo height={120} />
                <CircularProgress size={40} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                    <FormattedMessage
                        id="signingIn"
                        defaultMessage="Signing in..."
                    />
                </Typography>

                <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 4, opacity: 0.7 }}
                >
                    <FormattedMessage
                        id="versionInfo"
                        defaultMessage="Version {version}"
                        values={{ version: AppConfig.version }}
                    />
                </Typography>

                {errorMsg && (
                    <Box
                        position="absolute"
                        bottom={20}
                        width="100%"
                        textAlign="center"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
                            {errorMsg}
                        </Alert>
                        <Typography
                            variant="body2"
                            color="primary"
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                            onClick={handleRetry}
                        >
                            <FormattedMessage
                                id="retry"
                                defaultMessage="Try again"
                            />
                        </Typography>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default LoginPage;
